import { useEffect, useState } from "react";

/**
 * The banner a merchant sees while support is acting as them.
 *
 * **It cannot be dismissed, and it is above everything.** A support agent who
 * forgot they were impersonating would act as somebody else believing they were
 * themselves; a merchant who could close the banner would lose the only thing
 * on the screen saying their shop is being operated by someone from outside it.
 * So there is no close button, and the element sticks to the top of the
 * viewport rather than scrolling away. Sticky, not fixed: it takes its own
 * height, so it never covers the top bar's buttons beneath it.
 *
 * **The countdown is the reassurance.** "Ends in 04:12" says the window is
 * finite and how finite; a banner without it would read as a state somebody has
 * to end rather than one that ends itself.
 *
 * Shared because it belongs to both shells: EP-00 owns the Store Portal's
 * scaffold, and this is what it renders. The Admin Console never shows it — an
 * impersonation token cannot reach the console at all.
 */

export interface ImpersonationBannerProps {
	/** Who is acting. Shown so the merchant knows which company is in their shop. */
	operator: string;
	/** Who they are acting as. */
	target: string;
	/** When the window closes. */
	endsAt: string;
	/** Ends the session now. */
	onEnd: () => void;
	/** Whether the End request is in flight. */
	ending?: boolean;
	t: (key: string, values?: Record<string, string | number>) => string;
	/** The clock, for tests. */
	now?: () => Date;
}

export function ImpersonationBanner({
	operator,
	target,
	endsAt,
	onEnd,
	ending = false,
	t,
	now = () => new Date(),
}: ImpersonationBannerProps) {
	const [remaining, setRemaining] = useState(() => secondsUntil(endsAt, now()));

	useEffect(() => {
		// A second, because the thing being counted is displayed to the second.
		// Anything coarser makes the last minute look stuck.
		const timer = setInterval(() => setRemaining(secondsUntil(endsAt, now())), 1000);
		return () => clearInterval(timer);
	}, [endsAt, now]);

	return (
		<div
			// `role="status"` rather than `alert`: it is a standing condition,
			// not an interruption, and an assertive live region that re-read
			// itself every second would make the portal unusable with a screen
			// reader.
			role="status"
			className="sticky top-0 z-50 flex flex-wrap items-center justify-between gap-2 bg-warning-fg px-4 py-2 text-warning-bg"
		>
			<p className="text-[13px] font-bold">
				{t("portal.impersonation.acting", { operator, target })}
				<span className="ms-2 font-medium tabular-nums">
					{t("portal.impersonation.endsIn", { remaining: formatRemaining(remaining) })}
				</span>
			</p>

			<button
				type="button"
				onClick={onEnd}
				disabled={ending}
				// No dimming while the End request is in flight. The banner sits
				// on an inverted surface where `fg-subtle` does not apply, and
				// fading text over it is a contrast failure nobody measured —
				// for a state that lasts a fraction of a second and ends with
				// the banner gone. The cursor is the whole feedback.
				className="cursor-pointer rounded-sm border border-warning-bg px-2 py-1 text-[12.5px] font-bold disabled:cursor-not-allowed"
			>
				{t("portal.impersonation.end")}
			</button>
		</div>
	);
}

/** Whole seconds left, never negative. */
export function secondsUntil(endsAt: string, now: Date): number {
	const left = Math.floor((new Date(endsAt).getTime() - now.getTime()) / 1000);
	return left > 0 ? left : 0;
}

/**
 * `mm:ss`, with Latin digits.
 *
 * Formatted by hand rather than through `Intl`: this is a duration rather than
 * a time of day, and every locale's clock formatting would put something
 * different around it. Latin digits for the same reason the rest of the console
 * pins them.
 */
export function formatRemaining(seconds: number): string {
	const minutes = Math.floor(seconds / 60);
	const rest = seconds % 60;
	return `${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
}
